/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { 
  ArrowLeft, 
  Eye,
  CheckCircle2, 
  Clock, 
  AlertTriangle, 
  Camera, 
  FileUp, 
  Save, 
  Send, 
  X,
  Info,
  ChevronRight,
  FlaskConical,
  Beaker,
  History,
  Scan,
  Maximize2
} from 'lucide-react';
import { SterilityTestServices } from '../../../service/sterilityTestServices';
import { helperFunctions } from '../../../utils/helper';
import { DEFAULT_PAGE_SIZE } from '../../../constants/constants';

interface ParentTestTube {
  id: string;          // tube serial number (display + selection key)
  tubeId: string;      // backend tube_id (use when saving)
  media: string;
  mediaBatch: string;
  status: string;
  type: string;
  scheduleId: string;  // schedule row id (unique key + selection key)
  observationDay: string;
}

interface ParentTest {
  testId: string;
  productName: string;
  productBatch: string;
  testMethod: string;
  lotNumber: string;
  observationDay: string;
  status: string;
  tubes: ParentTestTube[];
}

const EMPTY_PARENT_TEST: ParentTest = {
  testId: '-',
  productName: '-',
  productBatch: '-',
  testMethod: '-',
  lotNumber: '-',
  observationDay: '-',
  status: '-',
  tubes: [],
};

const EMPTY_TUBE: ParentTestTube = {
  id: '-', tubeId: '', media: '-', mediaBatch: '-', status: '-', type: '-', scheduleId: '', observationDay: '-',
};

const isScheduleCompleted = (record: any) =>
  String(record?.schedule_status ?? '').toUpperCase() === 'COMPLETED';

// "07-10-2026 13:01:54" -> Date
const parseApiDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const [datePart, timePart = '00:00:00'] = String(value).split(' ');
  const [dd, mm, yyyy] = datePart.split('-').map(Number);
  const [hh, mi, ss] = timePart.split(':').map(Number);
  if (!dd || !mm || !yyyy) return null;
  return new Date(yyyy, mm - 1, dd, hh || 0, mi || 0, ss || 0);
};

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

// Day to record: today's pending schedule -> else first pending -> else the last schedule
const getCurrentObservationDay = (records: any[]): string => {
  if (!Array.isArray(records) || records.length === 0) return '';
  const today = new Date();
  const todays = records.find((r) => {
    const due = parseApiDate(r.scheduled_date_time);
    return !isScheduleCompleted(r) && due && isSameDay(due, today);
  });
  const pending = records.find((r) => !isScheduleCompleted(r));
  return String((todays ?? pending ?? records[records.length - 1]).observation_day);
};

const getTubeStatus = (row: any): string => {
  if (isScheduleCompleted(row)) return 'Completed';
  const due = parseApiDate(row.scheduled_date_time);
  return due && due <= new Date() ? 'Observation Due' : 'Pending';
};

// Builds the parentTest object from the observation schedule API rows.
// API rows are one per (tube x observation day) -> tubes list shows only the current day's rows.
const buildParentTest = (records: any[]): ParentTest => {
  if (!Array.isArray(records) || records.length === 0) return EMPTY_PARENT_TEST;

  // Header values are the same on every row of a test, so the first row is enough
  const first = records[0];
  const currentDay = getCurrentObservationDay(records);
  const dayRows = records.filter((r) => String(r.observation_day) === currentDay);

  const tubes: ParentTestTube[] = dayRows.map((row) => ({
    id: row.mt_serial_number ?? String(row.tube_id),   // shown in UI
    tubeId: String(row.tube_id),                       // backend id, use when saving
    media: row.media_name ?? '-',                      // not in current API response
    mediaBatch: row.mt_batch_number ?? '-',
    status: getTubeStatus(row),
    type: row.media_type_name ?? '-',                  // not in current API response
    scheduleId: String(row.schedule_id),               // use when saving
    observationDay: `Day ${row.observation_day}`,
  }));

  return {
    testId: first.sterility_test_identifier ?? '-',
    productName: first.product_name ?? '-',
    productBatch: first.product_batch_number ?? '-',   // not in current API response
    testMethod: first.test_method_name ?? '-',         // not in current API response
    lotNumber: first.production_schedule_name ?? '-',
    observationDay: currentDay ? `Day ${currentDay}` : '-',
    status: dayRows.some((r) => !isScheduleCompleted(r)) ? 'In Progress' : 'Completed',
    tubes,
  };
};

interface SterilityObservationRecordingProps {
  onBack: () => void;
  onSave: (data: any) => void;
  sterilityTestId: string;
  observationScheduleId: string
}

const SterilityObservationRecording: React.FC<SterilityObservationRecordingProps> = ({ onBack, onSave, sterilityTestId, observationScheduleId }) => {
  const { t } = useTranslation();

  // Raw observation schedule rows from the API
  const [scheduleRecords, setScheduleRecords] = useState<any[]>([]);
  const parentTest = useMemo(() => buildParentTest(scheduleRecords), [scheduleRecords]);

  // State
  const [selectedTubeId, setSelectedTubeId] = useState<string>('');
  const [observationResult, setObservationResult] = useState<'No Growth' | 'Growth Detected' | ''>('');
  const [visualClarity, setVisualClarity] = useState<string>('');
  const [indicators, setIndicators] = useState({
    colorChange: false,
    gasFormation: false,
    visibleParticles: false,
    surfaceFilm: false,
    sedimentFormation: false
  });
  const [remarks, setRemarks] = useState('');
  const [attachments, setAttachments] = useState<any[]>([]);
  // pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [sort, setSort] = useState("scheduled_date_time");
  const [order, setOrder] = useState("asc");
  const [sterilityObservationList, setSterilityObservationList] = useState([]);
  const [errors, setErrors] = useState({ observationResult: '', visualClarity: '', remarks: '' });
  
  // Computed Result State
  const [result, setResult] = useState<'PASS' | 'FAIL' | 'PENDING'>('PENDING');
  const [isAbnormal, setIsAbnormal] = useState(false);

  const selectedTube = parentTest.tubes.find(t => t.scheduleId === selectedTubeId) || parentTest.tubes[0] || EMPTY_TUBE;

  // Disposition details from the API row of the selected tube's schedule
  const selectedScheduleRow = useMemo(
    () => scheduleRecords.find((r) => String(r.schedule_id) === selectedTubeId),
    [scheduleRecords, selectedTubeId]
  );

  const dispositionTimestamp = selectedScheduleRow && isScheduleCompleted(selectedScheduleRow)
    ? helperFunctions.formatDate(selectedScheduleRow.observation_date_time)
    : '-';

  const dispositionOperator =
    selectedScheduleRow?.recorded_by_name ?? selectedScheduleRow?.schedule_operator_id ?? '-';

  // Timeline built from the observation schedule API rows (one entry per observation day)
  const timeline = useMemo(() => {
    const currentDay = getCurrentObservationDay(scheduleRecords);

    // a day can have several tube rows -> keep one entry per observation_day
    const byDay = new Map<string, any>();
    scheduleRecords.forEach((r) => {
      const key = String(r.observation_day);
      const existing = byDay.get(key);
      // day is "completed" only when every row of that day is completed
      if (!existing || (isScheduleCompleted(existing) && !isScheduleCompleted(r))) byDay.set(key, r);
    });

    return Array.from(byDay.values())
      .sort((a, b) => Number(a.observation_day) - Number(b.observation_day))
      .map((r) => {
        const completed = isScheduleCompleted(r);
        const isCurrent = !completed && String(r.observation_day) === currentDay;
        return {
          day: `Day ${r.observation_day}`,
          status: completed ? 'Completed' : isCurrent ? 'Current' : 'Pending',
          user: r.recorded_by_name ?? r.schedule_operator_id ?? '-',
          date: completed
            ? helperFunctions.formatDate(r.observation_date_time)
            : `${helperFunctions.formatDate(r.scheduled_date_time)} (Scheduled)`,
        };
      });
  }, [scheduleRecords]);

  // const timeline = [
  //   { day: 'Day 3', status: 'Completed', user: 'P Shyam Kumar', date: '15-Aug 09:30' },
  //   { day: 'Day 7', status: 'Current', user: '-', date: '-' },
  //   { day: 'Day 14', status: 'Pending', user: '-', date: '26-Aug-2024 (Scheduled)' }
  // ];
  

  // Progress = completed schedules / total schedules
  const completedCount = scheduleRecords.filter(isScheduleCompleted).length;
  const progressPercent = scheduleRecords.length
    ? Math.round((completedCount / scheduleRecords.length) * 100)
    : 0;

  const getSterilityObservationList = async (sterilityTestId: string) => {
    try {
      const response = await SterilityTestServices.getSterilityObservationList(page, pageSize, sort, order, sterilityTestId);

      if (response.status && response.statusCode === 200) {
        if (Array.isArray(response.data) && response.data.length > 0) {
          setTotalPages(response.totalPages ?? 0);
          setTotalElements(response.totalElements ?? 0);

          const rowData = response.data.map((element: any) => {
            return {
              id: element.schedule_id,
              testId: element.sterility_test_identifier,
              productionSchedule: element.production_schedule_name ?? '-',
              productName: element.product_name ?? '-',
              productBatch: element.mt_batch_number ?? '-',
              testMethod: element.test_method_name ?? '-',
              tubeCount: element.tube_count ?? 1,
              observationDay: element.observation_day ? `Day ${element.observation_day}` : '-',
              plannedDate: helperFunctions.formatDate(element.scheduled_date_time),
              actualDate: helperFunctions.formatDate(element.observation_date_time),
              result: element.observation_results ?? '-',
              status: element.schedule_status,
              recordedBy: element.recorded_by_name ?? '-',

              scheduleId: element.schedule_id,
              incubationId: element.incubation_id,
              sterilityTestId: element.sterility_test_id,
              tubeId: element.tube_id,
              tubeSerialNumber: element.mt_serial_number,
              operatorId: element.schedule_operator_id,
            };
          });

          setSterilityObservationList(rowData);
          setScheduleRecords(response.data);
        } else {
          setSterilityObservationList([]);
          setScheduleRecords([]);
          setTotalPages(0);
          setTotalElements(0);
        }
      } else {
        setSterilityObservationList([]);
        setScheduleRecords([]);
        setTotalPages(0);
        setTotalElements(0);
      }
    } catch (error) {
      console.error('Sterility observation list error', error);
      setSterilityObservationList([]);
      setScheduleRecords([]);
    }
  };

  const validateObservation = () => {
    const newErrors = { observationResult: '', visualClarity: '', remarks: '' };
    let isValid = true;

    if (!observationResult) {
      newErrors.observationResult = 'Observation result is required';
      isValid = false;
    }

    if (!visualClarity) {
      newErrors.visualClarity = 'Visual clarity is required';
      isValid = false;
    }

    // Remarks required only when growth is detected
    if (observationResult === 'Growth Detected' && !remarks.trim()) {
      newErrors.remarks = 'Remarks are required when growth is detected';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const formatDateTime = (date: Date = new Date()): string => {
    const pad = (n: number) => String(n).padStart(2, '0');

    return (
      `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
      `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    );
  };

  const handleSave = async () => {
    if (!validateObservation()) return;

    const payload = {
      observation_schedule_id: observationScheduleId,
      visual_clarity_assessment: visualClarity,
      color_change: indicators.colorChange,
      visible_particles: indicators.visibleParticles,
      sediment_formation: indicators.sedimentFormation,
      gas_formation: indicators.gasFormation,
      surface_film: indicators.surfaceFilm,
      remarks: remarks.trim(),
      attachment_file_id: null,
      observation_results: observationResult === 'No Growth' ? 'NO_GROWTH' : 'GROWTH',
      observation_date_time: formatDateTime(), // e.g. "2026-10-07 14:32:08"
      schedule_status: "COMPLETED"
    };

    onSave(payload);
  };

  useEffect(() => {
    getSterilityObservationList(sterilityTestId);
  }, [sterilityTestId])

  // Select the first tube once the tubes are loaded
  useEffect(() => {
    if (parentTest.tubes.length > 0 && !parentTest.tubes.some(t => t.scheduleId === selectedTubeId)) {
      const due = parentTest.tubes.find(t => t.status === 'Observation Due') ?? parentTest.tubes[0];
      setSelectedTubeId(due.scheduleId);
    }
  }, [parentTest.tubes]);


  // Logic to calculate result
  useEffect(() => {
    const hasAbnormality = 
      observationResult === 'Growth Detected' ||
      visualClarity === 'Growth Observed' || 
      visualClarity === 'Turbid' ||
      indicators.colorChange || 
      indicators.gasFormation || 
      indicators.visibleParticles || 
      indicators.surfaceFilm || 
      indicators.sedimentFormation;

    setIsAbnormal(hasAbnormality);

    if (observationResult === '') {
      setResult('PENDING');
    } else if (observationResult === 'Growth Detected' || hasAbnormality) {
      setResult('FAIL');
    } else {
      setResult('PASS');
    }
  }, [observationResult, visualClarity, indicators]);

  const toggleIndicator = (key: keyof typeof indicators) => {
    setIndicators(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Completed': return 'bg-emerald-50 text-emerald-600 border-emerald-100';
      case 'Observation Due': return 'bg-blue-50 text-blue-600 border-blue-100 ring-4 ring-blue-50';
      default: return 'bg-slate-50 text-slate-500 border-slate-100';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/30 flex flex-col media-prep-scope">
      {/* Header Sticky Area */}
      <header className="bg-white border-b border-slate-200 px-8 py-4 sticky top-0 z-30 shadow-sm">
        <div className="max-w-[1600px] mx-auto flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button 
              onClick={onBack}
              className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400 hover:text-[#0F172A] hover:bg-slate-100 transition-all shadow-sm"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="space-y-0.5">
              <div className="flex items-center gap-3">
                <h1 className="text-lg font-black text-[#0F172A] tracking-tight">{parentTest.testId}</h1>
                <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border ${getStatusBadge(parentTest.status)}`}>
                  {parentTest.status}
                </span>
                <span className="px-2 py-0.5 bg-orange-500 text-white text-[9px] font-black rounded uppercase tracking-widest">
                  {t('sterilityObservationRecording.header.checkpoint', { day: parentTest.observationDay })}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-slate-400 font-bold text-[10px] uppercase tracking-tighter">
                <div className="flex items-center gap-1.5"><span className="text-slate-300">{t('sterilityObservationRecording.header.product')}</span> {parentTest.productName} ({parentTest.productBatch})</div>
                <div className="flex items-center gap-1.5"><span className="text-slate-300">{t('sterilityObservationRecording.header.method')}</span> {parentTest.testMethod}</div>
                {/* <div className="flex items-center gap-1.5"><span className="text-slate-300">{t('sterilityObservationRecording.header.lot')}</span> {parentTest.lotNumber}</div> */}
              </div>
            </div>
          </div>
          
          {/* <div className="flex-1 max-w-sm">
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-1 flex items-center gap-2 group focus-within:ring-4 focus-within:ring-orange-500/10 focus-within:border-orange-500 transition-all">
              <div className="flex items-center gap-2 pl-3 py-1.5 border-r border-slate-200 pr-3">
                <Scan className="w-4 h-4 text-orange-500" />
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">{t('sterilityObservationRecording.header.scanTube')}</span>
              </div>
              <div className="flex-1 relative flex items-center">
                <input 
                  type="text" 
                  placeholder={t('sterilityObservationRecording.header.scanTubePlaceholder')}
                  className="w-full bg-transparent border-none text-xs font-bold text-slate-700 outline-none placeholder:text-slate-300 px-2"
                />
              </div>
            </div>
          </div> */}

          {/* <div className="hidden xl:flex items-center gap-6 px-6 py-1 border-l border-slate-100">
             <div className="flex flex-col">
                <span className="text-[9px] font-black text-slate-400 uppercase">{t('sterilityObservationRecording.header.tubesLoaded')}</span>
                <span className="text-xs font-black text-slate-700">{t('sterilityObservationRecording.header.units', { total: new Set(parentTest.tubes.map(t => t.tubeId)).size })}</span>
             </div>
             <div className="flex flex-col">
                <span className="text-[9px] font-black text-slate-400 uppercase">{t('sterilityObservationRecording.header.remaining')}</span>
                <span className="text-xs font-black text-orange-600">{t('sterilityObservationRecording.header.pendingCount')}</span>
             </div>
          </div> */}
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 max-w-[1600px] mx-auto w-full p-6 grid grid-cols-1 xl:grid-cols-12 gap-6 pb-32">
        
        {/* Left Panel - Tube List */}
        <div className="xl:col-span-3 space-y-4">
          <div className="bg-white rounded-[2rem] border border-slate-200/60 overflow-hidden shadow-sm sticky top-24">
            <div className="p-5 border-b border-slate-50 bg-slate-50/50">
              <h3 className="text-xs font-black text-[#0F172A] uppercase tracking-widest flex items-center gap-2">
                <Beaker className="w-4 h-4 text-orange-500" />
                {t('sterilityObservationRecording.tubeList.title')}
              </h3>
            </div>
            
            <div className="p-3 space-y-2 max-h-[calc(100vh-250px)] overflow-y-auto custom-scrollbar">
              {parentTest.tubes.map((tube) => (
                <button
                  key={tube.scheduleId}
                  onClick={() => setSelectedTubeId(tube.scheduleId)}
                  className={`w-full text-left p-4 rounded-2xl border transition-all relative group
                    ${selectedTubeId === tube.scheduleId 
                      ? 'bg-[#0F172A] border-[#0F172A] text-white shadow-lg' 
                      : 'bg-white border-slate-100 hover:border-slate-200 hover:bg-slate-50'
                    }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-[10px] font-black uppercase tracking-widest ${selectedTubeId === tube.scheduleId ? 'text-slate-400' : 'text-slate-400'}`}>
                      {tube.media}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-tighter border
                      ${tube.status === 'Completed' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 
                        tube.status === 'Observation Due' ? 'bg-blue-500/10 text-blue-500 border-blue-500/20' : 
                        'bg-slate-100 text-slate-400 border-slate-200'
                      }`}
                    >
                      {tube.status}
                    </span>
                  </div>
                  <h4 className="text-sm font-black tracking-tight">{tube.id}</h4>
                  <p className={`text-[9px] font-bold mt-1 uppercase ${selectedTubeId === tube.scheduleId ? 'text-slate-400' : 'text-slate-400'}`}>
                    {tube.type}
                  </p>
                  
                  {selectedTubeId === tube.scheduleId && (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2">
                      <ChevronRight className="w-4 h-4 text-white/50" />
                    </div>
                  )}
                </button>
              ))}
            </div>
            
            <div className="p-5 border-t border-slate-50 bg-slate-50/30">
               <div className="flex items-center gap-3">
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                     <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progressPercent}%` }}></div>
                  </div>
                  <span className="text-[10px] font-black text-slate-500">{progressPercent}%</span>
               </div>
            </div>
          </div>
        </div>

        {/* Center Panel - Main Recording Form */}
        <div className="xl:col-span-6 space-y-4">
          {/* Active Tube Info Header - Compact */}
          <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('sterilityObservationRecording.tubeInfo.testTubeId')}</p>
                <p className="text-sm font-black text-[#0F172A]">{selectedTube.id}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('sterilityObservationRecording.tubeInfo.mediaType')}</p>
                <p className="text-sm font-black text-[#0F172A]">{selectedTube.media}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('sterilityObservationRecording.tubeInfo.mediaLot')}</p>
                <p className="text-sm font-black text-[#0F172A]">{selectedTube.mediaBatch}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('sterilityObservationRecording.tubeInfo.observationDay')}</p>
                <p className="text-sm font-black text-orange-600 uppercase">{selectedTube.observationDay}</p>
              </div>
            </div>
            {/* <div className="mt-4 pt-4 border-t border-slate-50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-slate-300" />
                <span className="text-[10px] font-bold text-slate-400 uppercase">{t('sterilityObservationRecording.tubeInfo.recordingTime')}</span>
              </div>
              <span className="px-2 py-0.5 bg-slate-50 text-slate-400 text-[9px] font-black rounded uppercase tracking-tighter border border-slate-100">
                {t('sterilityObservationRecording.tubeInfo.limsSyncActive')}
              </span>
            </div> */}
          </div>

          {/* Recording Form Card */}
          <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-slate-50 bg-slate-50/30">
              <h3 className="text-xs font-black text-[#0F172A] uppercase tracking-widest flex items-center gap-2">
                <FlaskConical className="w-4 h-4 text-[#0F172A]" />
                {t('sterilityObservationRecording.form.title')}
              </h3>
            </div>

            <div className="p-8 space-y-8">
              {/* Result Toggle */}
              <div className="space-y-4">
                <label className="text-[10px] font-black text-slate-800 uppercase tracking-widest flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                  {t('sterilityObservationRecording.form.observationResult')}
                  <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setObservationResult('No Growth')}
                    className={`flex items-center justify-center gap-3 px-6 py-4 rounded-2xl text-xs font-black uppercase tracking-widest transition-all border-2
                      ${observationResult === 'No Growth' 
                        ? 'bg-emerald-500 border-emerald-500 text-white shadow-lg shadow-emerald-500/20' 
                        : 'bg-white border-slate-100 text-slate-400 hover:bg-slate-50'
                      }`}
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    {t('sterilityObservationRecording.form.noGrowth')}
                  </button>
                  <button
                    onClick={() => setObservationResult('Growth Detected')}
                    className={`flex items-center justify-center gap-3 px-6 py-4 rounded-2xl text-xs font-black uppercase tracking-widest transition-all border-2
                      ${observationResult === 'Growth Detected' 
                        ? 'bg-rose-500 border-rose-500 text-white shadow-lg shadow-rose-500/20' 
                        : 'bg-white border-slate-100 text-slate-400 hover:bg-slate-50'
                      }`}
                  >
                    <AlertTriangle className="w-4 h-4" />
                    {t('sterilityObservationRecording.form.growthDetected')}
                  </button>
                </div>
                {errors.observationResult && (
                  <p className="text-[10px] font-bold text-rose-500">{errors.observationResult}</p>
                )}
              </div>

              {/* Visual Clarity */}
              <div className="space-y-4">
                <label className="text-[10px] font-black text-slate-800 uppercase tracking-widest flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                  {t('sterilityObservationRecording.form.visualClarity')}
                  <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {['Clear', 'Slight Turbidity', 'Turbid', 'Opaque'].map((option) => (
                    <button
                      key={option}
                      onClick={() => setVisualClarity(option)}
                      className={`px-3 py-3 rounded-xl text-[10px] font-black uppercase transition-all border
                        ${visualClarity === option 
                          ? 'bg-[#0F172A] text-white border-[#0F172A]' 
                          : 'bg-slate-50 text-slate-400 border-transparent hover:bg-slate-100'
                        }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
                {errors.visualClarity && (
                  <p className="text-[10px] font-bold text-rose-500">{errors.visualClarity}</p>
                )}
              </div>

              {/* Contamination Indicators */}
              <div className="space-y-4">
                <label className="text-[10px] font-black text-slate-800 uppercase tracking-widest flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                  {t('sterilityObservationRecording.form.contaminationIndicators')}
                </label>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4 p-5 bg-slate-50/50 rounded-2xl border border-slate-100">
                  {[
                    { key: 'colorChange', label: t('sterilityObservationRecording.form.indicators.colorChange') },
                    { key: 'gasFormation', label: t('sterilityObservationRecording.form.indicators.gasFormation') },
                    { key: 'visibleParticles', label: t('sterilityObservationRecording.form.indicators.particles') },
                    { key: 'surfaceFilm', label: t('sterilityObservationRecording.form.indicators.surfaceFilm') },
                    { key: 'sedimentFormation', label: t('sterilityObservationRecording.form.indicators.sediment') }
                  ].map((item) => (
                    <div key={item.key} className="flex items-center justify-between">
                      <span className="text-[10px] font-bold text-slate-600 uppercase tracking-tight">{item.label}</span>
                      <button
                        onClick={() => toggleIndicator(item.key as keyof typeof indicators)}
                        className={`w-10 h-5 rounded-full relative transition-all duration-300 ${indicators[item.key as keyof typeof indicators] ? 'bg-orange-500 shadow-sm shadow-orange-500/20' : 'bg-slate-200'}`}
                      >
                        <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all duration-300 ${indicators[item.key as keyof typeof indicators] ? 'left-[22px]' : 'left-0.5'}`} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Remarks */}
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-800 uppercase tracking-widest flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                  {t('sterilityObservationRecording.form.technicianRemarks')}
                </label>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  className="w-full h-32 p-4 bg-slate-50 border border-slate-100 rounded-2xl text-xs font-medium focus:ring-4 focus:ring-orange-500/5 focus:border-orange-500 outline-none transition-all placeholder:text-slate-300"
                  placeholder={t('sterilityObservationRecording.form.remarksPlaceholder')}
                />
                {errors.remarks && (
                  <p className="text-[10px] font-bold text-rose-500">{errors.remarks}</p>
                )}
              </div>

              {/* Internal Actions */}
              {/* <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-50">
                <button
                  onClick={onBack}
                  className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-slate-600 transition-colors"
                >
                  {t('sterilityObservationRecording.actions.cancel')}
                </button>
                <button
                  onClick={() => handleSave()}
                  className='className="px-8 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg transition-all bg-[#0F172A] text-white hover:scale-105 active:scale-95 shadow-slate-900/10"'
                >
                  {t('sterilityObservationRecording.actions.saveObservation')}
                </button>
              </div> */}
            </div>
          </div>
        </div>

        {/* Right Panel - Result Preview */}
        <div className="xl:col-span-3 space-y-6">
          <div className="bg-white rounded-[2rem] border border-slate-200/60 p-6 shadow-sm">
            <h3 className="text-xs font-black text-[#0F172A] uppercase tracking-widest mb-8 flex items-center gap-2">
              <History className="w-4 h-4 text-orange-500" />
              {t('sterilityObservationRecording.timeline.title')}
            </h3>
            
            <div className="space-y-10 relative before:absolute before:left-[15px] before:top-2 before:bottom-2 before:w-[2px] before:bg-slate-100">
              {timeline.map((step, idx) => (
                <div key={idx} className="relative pl-10">
                  <div className={`absolute left-0 top-1.5 w-8 h-8 rounded-full border-4 border-white shadow-md flex items-center justify-center z-10 transition-all ${
                    step.status === 'Completed' ? 'bg-emerald-500 text-white' :
                    step.status === 'Current' ? 'bg-white text-blue-600 ring-4 ring-blue-50' :
                    'bg-slate-100 text-slate-300'
                  }`}>
                    {step.status === 'Completed' ? <CheckCircle2 className="w-4 h-4" /> : <div className={`w-2 h-2 rounded-full ${step.status === 'Current' ? 'bg-blue-600 animate-pulse' : 'bg-slate-300'}`} />}
                  </div>
                  <div className="space-y-1">
                    <p className={`text-[11px] font-black uppercase tracking-widest ${
                      step.status === 'Completed' ? 'text-slate-800' :
                      step.status === 'Current' ? 'text-blue-700' :
                      'text-slate-300'
                    }`}>{step.day}</p>
                    <p className="text-xs font-medium text-slate-500">{step.status === 'Current' ? t('sterilityObservationRecording.timeline.inProgress') : step.status}</p>
                    {step.status === 'Completed' && (
                      <div className="pt-2">
                         <p className="text-[9px] font-black text-slate-400 uppercase tracking-tight">{step.user}</p>
                         <p className="text-[9px] font-bold text-slate-400 italic">{step.date}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-[2rem] border border-slate-200/60 p-8 shadow-xl shadow-slate-200/20 sticky top-24">
             <div className="space-y-8">
                <div className="flex flex-col items-center justify-center py-8 bg-slate-50/50 rounded-3xl border border-slate-100">
                   <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{t('sterilityObservationRecording.disposition.title')}</p>
                   {result === 'PENDING' ? (
                     <div className="flex flex-col items-center gap-2">
                        <Clock className="w-12 h-12 text-slate-200 animate-pulse" />
                        <span className="text-lg font-black text-slate-300">{t('sterilityObservationRecording.disposition.awaiting')}</span>
                     </div>
                   ) : result === 'PASS' ? (
                     <div className="flex flex-col items-center gap-2">
                        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 shadow-lg shadow-emerald-500/10">
                           <CheckCircle2 className="w-10 h-10" />
                        </div>
                        <span className="text-2xl font-black text-emerald-600">{t('sterilityObservationRecording.disposition.pass')}</span>
                     </div>
                   ) : (
                     <div className="flex flex-col items-center gap-2">
                        <div className="w-16 h-16 rounded-full bg-rose-100 flex items-center justify-center text-rose-600 shadow-lg shadow-rose-500/10">
                           <AlertTriangle className="w-10 h-10" />
                        </div>
                        <span className="text-2xl font-black text-rose-600">{t('sterilityObservationRecording.disposition.fail')}</span>
                     </div>
                   )}
                </div>

                <div className="space-y-4 px-2">
                   <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest">
                      <span className="text-slate-400">{t('sterilityObservationRecording.disposition.timestamp')}</span>
                      <span className="text-slate-800">{dispositionTimestamp}</span>
                   </div>
                   <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest">
                      <span className="text-slate-400">{t('sterilityObservationRecording.disposition.operatorId')}</span>
                      <span className="text-slate-800">{dispositionOperator}</span>
                   </div>
                </div>

                <div className="bg-blue-50/50 p-4 rounded-2xl border border-blue-100/50 flex gap-3 items-start">
                   <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                   <p className="text-[10px] font-bold text-blue-700 leading-tight">
                     {t('sterilityObservationRecording.disposition.saveHint')}
                   </p>
                </div>
             </div>
          </div>
        </div>
      </main>

      {/* Bottom Action Bar */}
      <footer className="bg-white border-t border-slate-200 px-8 py-4 z-40 shadow-[0_-10px_20px_-15px_rgba(0,0,0,0.1)]">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between">
           <button 
             onClick={onBack}
             className="px-6 py-3 bg-white border border-slate-200 text-slate-500 text-xs font-black uppercase tracking-widest rounded-2xl hover:bg-slate-50 transition-all"
           >
             {t('sterilityObservationRecording.actions.cancel')}
           </button>
           
           <div className="flex items-center gap-4">
            
              <button 
                onClick={handleSave}
                className='flex items-center gap-2 px-8 py-3 rounded-2xl text-xs font-black uppercase tracking-widest transition-all shadow-xl bg-[#0F172A] text-white hover:scale-105 active:scale-95 shadow-slate-900/10'
                // className={`flex items-center gap-2 px-8 py-3 rounded-2xl text-xs font-black uppercase tracking-widest transition-all shadow-xl
                //   ${(observationResult === '' || (isAbnormal && remarks === ''))
                //     ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200 shadow-none'
                //     : 'bg-[#0F172A] text-white hover:scale-105 active:scale-95 shadow-slate-900/10'
                //   }`}
              >
                <CheckCircle2 className="w-4 h-4" />
                {t('sterilityObservationRecording.actions.saveObservation')}
              </button>
           </div>
        </div>
      </footer>
    </div>
  );
};

export default SterilityObservationRecording;